// Hintergrund-Vorlader für Videos (Jans Wunsch 26.08.2026).
//
// WARUM: Alle anderen Asset-Typen (Bilder, 3D, Texturen, HDRI, Schriften)
// lädt der Boot komplett VOR dem Start — nur Videos streamten und iOS-Safari
// puffert bei preload="auto" fast nichts, bis wirklich abgespielt wird. Wer
// schnell scrollte, sah schwarze Videoflächen (Jans bekanntes Website-Ärgernis).
//
// WIE: Nach dem START-Klick holt eine Warteschlange die Videos der Reihe nach
// (Scroll-Reihenfolge = Lebensdauer-Beginn) KOMPLETT als Blob und tauscht die
// Quelle des <video>-Elements auf die lokale Blob-URL — ab dann sind Suchen
// und Abspielen sofort, ohne Range-Requests. Der Start selbst wartet auf
// nichts; wer abspringt, hat nur die bis dahin geholten Videos bezahlt.
//
// SCHUTZPLANKEN:
// - Gesamt-Budget (mobil enger) und Einzeldatei-Deckel — ein 200-MB-Ausreißer
//   frisst nicht das Datenvolumen des Lesers.
// - Bereits angespielte Videos werden NICHT getauscht (der Tausch setzt
//   currentTime zurück; laufende Videos puffern ohnehin).
// - Fehler eines Videos brechen die Kette nicht — das Element behält einfach
//   seine Netz-URL (altes Verhalten).
// - Editor und lokale IndexedDB-Quellen ("local-video:") bleiben außen vor.
import { istMobilesGeraet } from "../utils/geraet";
import type Video from "./Video";

const EINZEL_DECKEL_BYTES = 80 * 1024 * 1024;
const GESAMT_BUDGET_BYTES = () => (istMobilesGeraet() ? 200 : 400) * 1024 * 1024;

/** Datensparmodus des Nutzers — dann bleibt es beim Streaming. */
const datensparmodus = () =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof navigator !== "undefined" && !!(navigator as any).connection?.saveData;

/**
 * Langsame Verbindung laut Browser (Network Information API: 2G/3G-Klasse).
 * Dann lohnt weder das Vorholen des Startvideos noch der Vorlader — jedes
 * Byte, das nicht dem gerade sichtbaren Video gehoert, verlaengert dessen
 * Wartezeit. Browser ohne die API melden nichts → normales Verhalten.
 */
export const langsameVerbindung = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const typ = typeof navigator !== "undefined" ? (navigator as any).connection?.effectiveType : undefined;
    return typ === "slow-2g" || typ === "2g" || typ === "3g";
};

/**
 * Deckel fuers Vorholen des STARTVIDEOS als Blob (Jans Slow-4G-Test am
 * WESER-KURIER-Scrolly, 05.09.2026): Das Startvideo dort hat 16,8 MB; der
 * Reader versuchte es 8 s lang komplett zu laden, brach ab, warf die Bytes
 * weg und streamte dann von vorn — waehrend das 1,5-MB-Video daneben laengst
 * lief. Ab dieser Groesse wird gar nicht erst vorgeholt, sondern sofort
 * gestreamt (das Poster deckt die Zeit bis zum ersten Bild). 4 MB sind auf
 * einem normalen Mobilnetz in 2–3 s da — die Schwelle, unter der das
 * Vorholen dem Start noch hilft.
 */
// 5 statt 4 MB (Benedikts Entscheidung 06.09.2026, Jans Vorschlag): die
// Eskalationsstufe der Video-Renditions liefert fuer bewegungsreiche iPhone-
// Startvideos Mobilfassungen um 4,5 MB (WK-Startvideo 16,1 → 4,6 MB bei SSIM
// 0,923). Mit 4 MB wuerden genau diese Clips gestreamt statt vorgeholt; die
// uebrigen Mobilfassungen liegen bei 1 bis 3 MB und sind nicht betroffen.
// Auf schlechter Verbindung faengt weiterhin der 8-s-Fallback den Start ab.
export const STARTVIDEO_BLOB_DECKEL_BYTES = 5 * 1024 * 1024;

/**
 * Ein Video KOMPLETT als Blob holen und als lokale URL zurueckgeben — der
 * Baustein, den auch das STARTVIDEO im Boot nutzt (Jans iPhone-Fund
 * 02.09.2026, „Video springt am Anfang / ruckelt beim Losfahren"):
 *
 * iOS-Safari puffert bei preload="auto" praktisch nichts, bis wirklich
 * abgespielt wird. Das Video auf dem Startbildschirm hatte beim START-Klick
 * deshalb noch keine Bilddaten — das Poster stand, der Decoder lief erst
 * in der Geste an, und der Vorlader zog dasselbe Video parallel ein
 * zweites Mal. Als Blob VOR dem Start geladen (wie jedes andere Asset,
 * mit im Ladebalken) spielt es beim Klick sofort lokal los.
 *
 * `null` = nicht vorladen (Datensparmodus, zu gross, Netz-/CORS-Fehler,
 * Zeitlimit) — der Aufrufer bleibt dann beim bisherigen Streaming. Das
 * Zeitlimit haelt den Boot-Watchdog (10 s) frei: lieber alt streamen als
 * den Start blockieren.
 */
export async function holeVideoAlsBlob(
    url: string,
    zeitlimitMs: number,
    /** Groessen-Deckel; darueber sofort streamen (s. STARTVIDEO_BLOB_DECKEL_BYTES). */
    maxBytes: number = STARTVIDEO_BLOB_DECKEL_BYTES
): Promise<{ blobUrl: string; groesse: number } | null> {
    if (typeof fetch === "undefined" || typeof URL === "undefined") return null;
    if (!/^https?:/.test(url) || datensparmodus() || langsameVerbindung()) return null;
    const abbruch = new AbortController();
    const timer = setTimeout(() => abbruch.abort(), zeitlimitMs);
    try {
        const antwort = await fetch(url, { mode: "cors", signal: abbruch.signal });
        if (!antwort.ok) return null;
        const laenge = Number(antwort.headers.get("content-length") || 0);
        // Zu gross fuers Vorholen: Body sofort verwerfen, Verbindung frei —
        // das Element streamt dann dieselbe Adresse mit Range-Requests.
        if (laenge > Math.min(maxBytes, EINZEL_DECKEL_BYTES)) {
            try {
                await antwort.body?.cancel();
            } catch {
                /* ignore */
            }
            return null;
        }
        const blob = await antwort.blob();
        return { blobUrl: URL.createObjectURL(blob), groesse: blob.size };
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
}

export interface VorladerEintrag {
    layerId: string;
    url: string;
    /** Lebensdauer-Beginn des Layers — bestimmt die Lade-Reihenfolge. */
    von: number;
}

export class VideoVorlader {
    gestartet = false;
    private abgebrochen = false;
    private blobUrls: string[] = [];

    start(eintraege: VorladerEintrag[], holeVideo: (layerId: string) => Video | undefined) {
        if (this.gestartet) return;
        this.gestartet = true;
        // Datensparmodus des Nutzers respektieren — dann bleibt es beim
        // bisherigen Streaming-Verhalten. Gleiches bei langsamer Verbindung
        // (2G/3G laut Browser): dort nimmt der Vorlader dem gerade laufenden
        // Video die Leitung weg (Jans Slow-4G-Test 05.09.2026 — Video 2 war
        // fertig, Video 1 hungerte).
        if (datensparmodus() || langsameVerbindung()) return;
        const sortiert = [...eintraege]
            .filter((e) => typeof e.url === "string" && /^https?:/.test(e.url))
            .sort((a, b) => a.von - b.von);
        void this.ladeReihe(sortiert, holeVideo);
    }

    dispose() {
        this.abgebrochen = true;
        this.blobUrls.forEach((u) => {
            try {
                URL.revokeObjectURL(u);
            } catch {
                /* ignore */
            }
        });
        this.blobUrls = [];
    }

    private async ladeReihe(
        eintraege: VorladerEintrag[],
        holeVideo: (layerId: string) => Video | undefined
    ) {
        let verbraucht = 0;
        for (const eintrag of eintraege) {
            if (this.abgebrochen) return;
            if (verbraucht >= GESAMT_BUDGET_BYTES()) return;
            // VOR dem Download pruefen, nicht erst danach (Jans iPhone-Fund
            // 02.09.2026): Das Startvideo liegt seit dem Boot als Blob vor bzw.
            // spielt bereits — es hier trotzdem komplett zu holen, nur um es
            // dann nicht zu tauschen, zog dieselbe Datei ein zweites Mal
            // ueber die Leitung, genau waehrend der Start losfaehrt.
            const vorab = holeVideo(eintrag.layerId)?.videoPlayer;
            if (
                vorab &&
                (vorab.src.startsWith("blob:") || !vorab.paused || vorab.currentTime > 0.05)
            ) {
                continue;
            }
            try {
                const antwort = await fetch(eintrag.url, { mode: "cors" });
                if (!antwort.ok) continue;
                const laenge = Number(antwort.headers.get("content-length") || 0);
                if (laenge > EINZEL_DECKEL_BYTES) {
                    // Body verwerfen, Verbindung freigeben
                    try {
                        await antwort.body?.cancel();
                    } catch {
                        /* ignore */
                    }
                    continue;
                }
                const blob = await antwort.blob();
                verbraucht += blob.size;
                if (this.abgebrochen) return;

                const video = holeVideo(eintrag.layerId);
                const spieler = video?.videoPlayer;
                if (!spieler) continue;
                // Schon angespielt/aktiv? Dann nicht mehr anfassen.
                if (!spieler.paused || spieler.currentTime > 0.05) continue;
                const blobUrl = URL.createObjectURL(blob);
                this.blobUrls.push(blobUrl);
                spieler.src = blobUrl;
                spieler.load();
            } catch {
                // Netz-/CORS-Fehler: Element behaelt seine Original-URL.
            }
        }
    }
}
